import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, X, Loader2, XCircle } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import RichTextEditor from '../../../components/common/RichTextEditor';
import jobService from '../../../api/services/jobService';
import departmentRoleService from '../../../api/services/departmentRoleService';
import locationService from '../../../api/services/locationService';
import { experienceService, skillService, requirementService } from '../../../api/crm/config/configService';
import { validateField, VALIDATION_RULES, ErrorMessage } from '../../../utils/formValidation';
import toast from 'react-hot-toast';

const CreateJob = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    type: 'Full-time',
    experience: '',
    experienceRef: '',
    preferredGender: 'any',
    salaryMin: '',
    salaryMax: '',
    description: '',
    requirements: [],
    skills: [],
    deadline: '',
    status: 'Draft',
    priority: 'Medium'
  });

  const [newRequirement, setNewRequirement] = useState('');
  const [newSkill, setNewSkill] = useState('');

  const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Remote'];
  const priorities = ['High', 'Medium', 'Low'];

  const [experienceLevels, setExperienceLevels] = useState([]);
  const [loadingExperiences, setLoadingExperiences] = useState(false);
  const [commonSkills, setCommonSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [commonRequirements, setCommonRequirements] = useState([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);

  // Queries
  const { data: departments = [], isLoading: isLoadingDepartments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const result = await departmentRoleService.getDepartments();
      let departmentsData = [];

      if (Array.isArray(result)) {
        departmentsData = result;
      } else if (result && result.data) {
        departmentsData = Array.isArray(result.data) ? result.data : result.data.departments || [];
      } else if (result && result.responseCode === 200) {
        departmentsData = result.data?.departments || result.data || [];
      }

      const getTimestamp = (dept) => {
        if (!dept || typeof dept === 'string') return null;
        const dateValue = dept.createdAt || dept.updatedAt || dept.created_at || dept.updated_at;
        if (!dateValue) return null;
        const timestamp = new Date(dateValue).getTime();
        return Number.isNaN(timestamp) ? null : timestamp;
      };

      const hasTimestamp = departmentsData.some(dept => getTimestamp(dept) !== null);
      const orderedDepartments = hasTimestamp
        ? [...departmentsData].sort((a, b) => (getTimestamp(b) ?? 0) - (getTimestamp(a) ?? 0))
        : [...departmentsData].reverse();

      return orderedDepartments.map(dept => {
        if (typeof dept === 'string') return dept;
        return dept?.name || dept?.departmentName || dept?.title || dept?.department || dept?._id || '';
      }).filter(Boolean);
    }
  });

  const { data: locations = [], isLoading: isLoadingLocations } = useQuery({
    queryKey: ['activeLocations', 'names'],
    queryFn: async () => {
      const result = await locationService.getActiveLocations();
      if (result.success) {
        return result.data.map(loc => loc.name || loc.label || loc.value);
      }
      return ['Mumbai Office', 'Delhi Office', 'Bangalore Office', 'Chennai Office', 'Remote'];
    }
  });

  // Load experience levels on component mount
  useEffect(() => {
    const loadExperiences = async () => {
      try {
        setLoadingExperiences(true);
        const result = await experienceService.getAll({ isActive: true });

        if (result && (result.success || result.responseCode === 200)) {
          const experienceData = result.data || [];
          setExperienceLevels(experienceData);
        } else {
          setExperienceLevels([]);
        }
      } catch (err) {
        setExperienceLevels([]);
      } finally {
        setLoadingExperiences(false);
      }
    };

    loadExperiences();
  }, []);

  // Load skills on component mount
  useEffect(() => {
    const loadSkills = async () => {
      try {
        setLoadingSkills(true);
        const result = await skillService.getAll({ isActive: true });

        if (result && (result.success || result.responseCode === 200)) {
          const skillsData = result.data || [];
          setCommonSkills(skillsData.map(s => s.name));
        } else {
          setCommonSkills([]);
        }
      } catch (err) {
        setCommonSkills([]);
      } finally {
        setLoadingSkills(false);
      }
    };

    loadSkills();
  }, []);

  // Load requirements on component mount
  useEffect(() => {
    const loadRequirements = async () => {
      try {
        setLoadingRequirements(true);
        const result = await requirementService.getAll({ isActive: true });

        if (result && (result.success || result.responseCode === 200)) {
          const requirementsData = result.data || [];
          setCommonRequirements(requirementsData.map(r => r.name));
        } else {
          setCommonRequirements([]);
        }
      } catch (err) {
        setCommonRequirements([]);
      } finally {
        setLoadingRequirements(false);
      }
    };

    loadRequirements();
  }, []);

  const { data: jobData, isLoading: isLoadingJob } = useQuery({
    queryKey: ['job', id],
    queryFn: async () => {
      const result = await jobService.getJobById(id);
      if (result.success) return result.data;
      throw new Error(result.error || 'Failed to fetch job');
    },
    enabled: isEditMode
  });

  // Prefill form when job data is loaded
  useEffect(() => {
    if (jobData && isEditMode) {
      setFormData({
        title: jobData.title || '',
        department: jobData.department || '',
        location: jobData.location || '',
        type: jobData.type === 'full_time' ? 'Full-time' : jobData.type === 'part_time' ? 'Part-time' : jobData.type.charAt(0).toUpperCase() + jobData.type.slice(1),
        experience: jobData.experience?.name || (jobData.experience ? (jobData.experience.min === 10 && !jobData.experience.max ? '10+ years' : `${jobData.experience.min}-${jobData.experience.max} years`) : ''),
        experienceRef: jobData.experienceRef || jobData.experience?.ref || '',
        preferredGender: jobData.preferredGender || 'any',
        salaryMin: jobData.salary?.min?.toString() || '',
        salaryMax: jobData.salary?.max?.toString() || '',
        description: jobData.description || '',
        requirements: jobData.requirements || [],
        skills: jobData.skills || [],
        deadline: jobData.deadline ? new Date(jobData.deadline).toISOString().split('T')[0] : '',
        status: jobData.status.charAt(0).toUpperCase() + jobData.status.slice(1),
        priority: jobData.priority.charAt(0).toUpperCase() + jobData.priority.slice(1)
      });
    }
  }, [jobData, isEditMode]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => jobService.createJob(data),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Job created successfully!', { duration: 3000 });
        queryClient.invalidateQueries(['jobs']);
        setTimeout(() => navigate(-1), 500);
      } else {
        toast.error('Error creating job: ' + result.error, { duration: 4000 });
      }
    },
    onError: (error) => {
      toast.error('Error creating job: ' + error.message, { duration: 4000 });
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data) => jobService.updateJob(id, data),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Job updated successfully!', { duration: 3000 });
        queryClient.invalidateQueries(['jobs']);
        queryClient.invalidateQueries(['job', id]);
        setTimeout(() => navigate(-1), 500);
      } else {
        toast.error('Error updating job: ' + result.error, { duration: 4000 });
      }
    },
    onError: (error) => {
      toast.error('Error updating job: ' + error.message, { duration: 4000 });
    }
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const getDepartmentLabel = (name, limit = 50) => {
    if (!name) return '';
    return name.length > limit ? `${name.slice(0, limit - 1)}…` : name;
  };

  const jobValidationRules = {
    title: {
      required: true,
      pattern: /^[a-zA-Z0-9\s,+/()\-]+$/,
      patternMessage: 'Job title can only contain alphanumeric characters, spaces, commas, plus signs, slashes, parentheses, and hyphens.',
      maxLength: 120,
      customLabel: 'Job title'
    },
    department: { required: true },
    location: { required: true },
    type: { required: true, customLabel: 'Job type' },
    experience: { required: true, customLabel: 'Experience level' },
    description: {
      required: true,
      minLength: 10,
      maxLength: 2000, // Logical text limit
      customLabel: 'Job description'
    },
    deadline: {
      required: true,
      customLabel: 'Application deadline'
    },
    status: { required: true },
    priority: { required: true }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'title') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,+/()\-]/g, '').substring(0, 120);
    } else if (name === 'salaryMin' || name === 'salaryMax') {
      let processed = value.replace(/[^0-9]/g, '');
      if (processed && Number(processed) > 100000000) processed = '100000000';
      processedValue = processed;
    } else if (name === 'deadline' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        toast.error('Application deadline cannot be in the past.');
        return;
      }
      processedValue = value;
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    const rules = jobValidationRules[name];
    let fieldError = '';

    if (rules) {
      const fieldErrors = validateField(name, value, rules);
      if (fieldErrors.length > 0) fieldError = fieldErrors[0];
    }

    if (!fieldError) {
      if (name === 'salaryMin' || name === 'salaryMax') {
        const minSalary = parseInt(formData.salaryMin) || 0;
        const maxSalary = parseInt(formData.salaryMax) || 0;
        const isMinZeroOrEmpty = !formData.salaryMin || minSalary === 0;
        const isMaxZeroOrEmpty = !formData.salaryMax || maxSalary === 0;

        if (!(isMinZeroOrEmpty && isMaxZeroOrEmpty)) {
          if (formData.salaryMin && formData.salaryMax && minSalary >= maxSalary) {
            fieldError = name === 'salaryMin' ? 'Min salary must be < max salary.' : 'Max salary must be > min salary.';
          }
        }
      }
    }

    setErrors(prev => ({ ...prev, [name]: fieldError }));
  };

  const addRequirement = () => {
    const filtered = newRequirement.replace(/[^a-zA-Z0-9\s+/.#,()&_\-:;'"@!*?%[\]<>=\^~|\\]/g, '');
    const trimmed = filtered.trim();

    const isDuplicate = formData.requirements.some(
      (req) => req.toLowerCase() === trimmed.toLowerCase()
    );

    if (trimmed) {
      if (isDuplicate) {
        setErrors((prev) => ({ ...prev, newRequirement: 'This requirement already exists.' }));
        return;
      }
      if (formData.requirements.length >= 30) {
        toast.error('Maximum 30 requirements allowed per job.');
        return;
      }
      if (trimmed.length < 3) {
        toast.error('Requirement must be at least 3 characters long.');
        return;
      }
      if (trimmed.length > 200) {
        toast.error('Requirement cannot exceed 200 characters.');
        return;
      }

      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, trimmed]
      }));
      setNewRequirement('');
      setErrors((prev) => ({ ...prev, newRequirement: null }));
    }
  };

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const addSkill = (skillArg) => {
    const skillName = typeof skillArg === 'string' ? skillArg : newSkill;
    const filtered = skillName.replace(/[^a-zA-Z0-9\s+/.#,()&_\-:;'"@!*?%[\]<>=\^~|\\]/g, '');
    const trimmed = filtered.trim();

    const isDuplicate = formData.skills.some(
      (s) => s.toLowerCase() === trimmed.toLowerCase()
    );

    if (trimmed) {
      if (isDuplicate) {
        setErrors((prev) => ({ ...prev, newSkill: 'This skill already exists.' }));
        return;
      }
      if (formData.skills.length >= 30) {
        toast.error('Maximum 30 skills allowed per job.');
        return;
      }
      if (trimmed.length < 2) {
        toast.error('Skill must be at least 2 characters long.');
        return;
      }
      if (trimmed.length > 200) {
        toast.error('Skill cannot exceed 200 characters.');
        return;
      }

      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, trimmed]
      }));
      setNewSkill('');
      setErrors((prev) => ({ ...prev, newSkill: null }));
    }
  };

  const removeSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    Object.keys(jobValidationRules).forEach(name => {
      let value = formData[name];
      const rules = jobValidationRules[name];

      if (name === 'description' && typeof value === 'string') {
        value = value.replace(/<[^>]*>/g, '').trim();
      }

      const fieldErrors = validateField(name, value, rules);
      if (fieldErrors.length > 0) {
        newErrors[name] = fieldErrors[0];
        isValid = false;
      }
    });

    const minSalary = parseInt(formData.salaryMin) || 0;
    const maxSalary = parseInt(formData.salaryMax) || 0;
    const isMinZeroOrEmpty = !formData.salaryMin || minSalary === 0;
    const isMaxZeroOrEmpty = !formData.salaryMax || maxSalary === 0;

    if (!(isMinZeroOrEmpty && isMaxZeroOrEmpty)) {
      if (formData.salaryMin && formData.salaryMax) {
        if (minSalary >= maxSalary) {
          newErrors.salaryMax = 'Maximum salary must be greater than minimum salary.';
          isValid = false;
        }
      } else if (formData.salaryMin && !formData.salaryMax) {
        newErrors.salaryMax = 'Please enter maximum salary or clear minimum salary.';
        isValid = false;
      } else if (!formData.salaryMin && formData.salaryMax) {
        newErrors.salaryMin = 'Please enter minimum salary or clear maximum salary.';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix validation errors.');
      return;
    }

    const jobData = {
      title: formData.title,
      department: formData.department,
      location: formData.location,
      type: formData.type === 'Full-time' ? 'full_time' : formData.type === 'Part-time' ? 'part_time' : formData.type.toLowerCase(),
      experience: {
        min: (() => {
          const selectedExp = experienceLevels.find(exp => exp.name === formData.experience);
          if (selectedExp) return selectedExp.minYear || 0;
          return parseInt(formData.experience.split('-')[0]) || 0;
        })(),
        max: (() => {
          const selectedExp = experienceLevels.find(exp => exp.name === formData.experience);
          if (selectedExp) return selectedExp.maxYear || 0;
          return parseInt(formData.experience.split('-')[1]) || 0;
        })(),
        name: formData.experience,
        ref: formData.experienceRef || null
      },
      experienceRef: formData.experienceRef || null,
      preferredGender: formData.preferredGender,
      salary: {
        min: parseInt(formData.salaryMin) || 0,
        max: parseInt(formData.salaryMax) || 0
      },
      description: formData.description,
      requirements: formData.requirements,
      skills: formData.skills,
      deadline: formData.deadline,
      status: formData.status.toLowerCase(),
      priority: formData.priority.toLowerCase()
    };

    if (isEditMode) {
      updateMutation.mutate(jobData);
    } else {
      createMutation.mutate(jobData);
    }
  };

  if (isLoadingJob && isEditMode) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-agrosaf-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Job Posting' : 'Create Job Posting'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditMode ? 'Update the details of your job posting' : 'Create a new job posting for recruitment'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Job Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                maxLength={120}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="e.g., Senior Software Engineer"
              />
              <div className="flex justify-between items-center mt-1">
                <ErrorMessage error={errors.title} />
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.title.length}/120 characters
                </p>
              </div>
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isLoadingDepartments}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.department ? 'border-red-300' : 'border-gray-300'
                  } ${isLoadingDepartments ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">
                  {isLoadingDepartments ? 'Loading departments...' : 'Select Department'}
                </option>
                {departments.map(dept => (
                  <option key={dept} value={dept} title={dept}>
                    {getDepartmentLabel(dept)}
                  </option>
                ))}
              </select>
              <div className="mt-1">
                <ErrorMessage error={errors.department} />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <select
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.location ? 'border-red-300' : 'border-gray-300'
                  }`}
                disabled={isLoadingLocations}
              >
                <option value="">
                  {isLoadingLocations ? 'Loading locations...' : 'Select Location'}
                </option>
                {locations.map(location => {
                  const safeLocation = typeof location === 'string' ? location : (location?.name || location?.label || location?.value || String(location || ''));
                  return (
                    <option key={safeLocation} value={safeLocation}>
                      {safeLocation ? safeLocation.charAt(0).toUpperCase() + safeLocation.slice(1) : ''}
                    </option>
                  );
                })}
              </select>
              <div className="mt-1">
                <ErrorMessage error={errors.location} />
              </div>
            </div>

            {/* Job Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Type <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.type ? 'border-red-300' : 'border-gray-300'
                  }`}
              >
                {jobTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <div className="mt-1">
                <ErrorMessage error={errors.type} />
              </div>
            </div>

            {/* Experience Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Experience Level <span className="text-red-500">*</span>
              </label>
              <select
                name="experience"
                value={formData.experience}
                onChange={(e) => {
                  const selectedExp = experienceLevels.find(exp => exp.name === e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    experience: e.target.value,
                    experienceRef: selectedExp ? (selectedExp._id || selectedExp.id) : ""
                  }));
                  if (errors.experience) {
                    setErrors(prev => ({ ...prev, experience: "" }));
                  }
                }}
                onBlur={handleInputBlur}
                disabled={loadingExperiences}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.experience ? 'border-red-300' : 'border-gray-300'
                  } ${loadingExperiences ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">
                  {loadingExperiences ? 'Loading experience levels...' : 'Select Experience'}
                </option>
                {experienceLevels.map((exp) => (
                  <option key={exp._id || exp.id} value={exp.name}>
                    {exp.name}
                  </option>
                ))}
              </select>
              <div className="mt-1">
                <ErrorMessage error={errors.experience} />
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.priority ? 'border-red-300' : 'border-gray-300'
                  }`}
              >
                {priorities.map(priority => (
                  <option key={priority} value={priority}>{priority} Priority</option>
                ))}
              </select>
              <div className="mt-1">
                <ErrorMessage error={errors.priority} />
              </div>
            </div>

            {/* Preferred Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Gender
              </label>
              <div className="flex items-center space-x-6 h-10">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="preferredGender"
                    value="any"
                    checked={formData.preferredGender === 'any'}
                    onChange={handleInputChange}
                    className="form-radio text-agrosaf-green focus:ring-agrosaf-green h-4 w-4"
                  />
                  <span className="ml-2 text-sm text-gray-700">Any</span>
                </label>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="preferredGender"
                    value="male"
                    checked={formData.preferredGender === 'male'}
                    onChange={handleInputChange}
                    className="form-radio text-agrosaf-green focus:ring-agrosaf-green h-4 w-4"
                  />
                  <span className="ml-2 text-sm text-gray-700">Male</span>
                </label>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="preferredGender"
                    value="female"
                    checked={formData.preferredGender === 'female'}
                    onChange={handleInputChange}
                    className="form-radio text-agrosaf-green focus:ring-agrosaf-green h-4 w-4"
                  />
                  <span className="ml-2 text-sm text-gray-700">Female</span>
                </label>
              </div>
            </div>

            {/* Salary Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salary Range
              </label>
              <div className="flex space-x-2">
                <div className="flex-1">
                  <input
                    type="number"
                    name="salaryMin"
                    value={formData.salaryMin}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={(e) => {
                      // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
                      if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
                        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                        (e.keyCode === 65 && e.ctrlKey === true) ||
                        (e.keyCode === 67 && e.ctrlKey === true) ||
                        (e.keyCode === 86 && e.ctrlKey === true) ||
                        (e.keyCode === 88 && e.ctrlKey === true) ||
                        // Allow numbers on top of keyboard
                        (e.keyCode >= 48 && e.keyCode <= 57) ||
                        // Allow numbers on numpad
                        (e.keyCode >= 96 && e.keyCode <= 105)) {
                        return;
                      }
                      // Block: minus, plus, e, E, period (for decimal)
                      if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
                        e.preventDefault();
                      }
                    }}
                    min="0"
                    max="100000000"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.salaryMin ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Min"
                  />
                  <div className="mt-1">
                    <ErrorMessage error={errors.salaryMin} />
                  </div>
                </div>
                <span className="flex items-center text-gray-500">to</span>
                <div className="flex-1">
                  <input
                    type="number"
                    name="salaryMax"
                    value={formData.salaryMax}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={(e) => {
                      // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
                      if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
                        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                        (e.keyCode === 65 && e.ctrlKey === true) ||
                        (e.keyCode === 67 && e.ctrlKey === true) ||
                        (e.keyCode === 86 && e.ctrlKey === true) ||
                        (e.keyCode === 88 && e.ctrlKey === true) ||
                        // Allow numbers on top of keyboard
                        (e.keyCode >= 48 && e.keyCode <= 57) ||
                        // Allow numbers on numpad
                        (e.keyCode >= 96 && e.keyCode <= 105)) {
                        return;
                      }
                      // Block: minus, plus, e, E, period (for decimal)
                      if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
                        e.preventDefault();
                      }
                    }}
                    min="0"
                    max="100000000"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.salaryMax ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Max"
                  />
                  <div className="mt-1">
                    <ErrorMessage error={errors.salaryMax} />
                  </div>
                </div>
              </div>
            </div>

            {/* Application Deadline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Application Deadline <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="deadline"
                value={formData.deadline}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent cursor-pointer ${errors.deadline ? 'border-red-300' : 'border-gray-300'
                  }`}
                onClick={(e) => {
                  try {
                    e.target.showPicker();
                  } catch (err) {
                  }
                }}
              />
              <div className="flex justify-between items-center mt-1">
                <ErrorMessage error={errors.deadline} />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.status ? 'border-red-300' : 'border-gray-300'
                  }`}
              >
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
                <option value="Closed">Closed</option>
              </select>
              <div className="mt-1">
                <ErrorMessage error={errors.status} />
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Description <span className="text-red-500">*</span>
            </label>
            <div className={`rounded-lg border-2 border-gray-300 overflow-hidden ${errors.description ? 'ring-2 ring-red-500' : ''}`}>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => {
                  // Strip HTML tags and common entities to get actual text length
                  const plainText = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
                  const currentPlainText = formData.description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');

                  // Enforce limits: 2000 for text, 5000 for total HTML
                  if ((plainText.length <= 2000 && value.length <= 5000) || plainText.length < currentPlainText.length) {
                    setFormData(prev => ({
                      ...prev,
                      description: value
                    }));

                    // Clear error when user starts typing and is within limit
                    if (errors.description && plainText.length <= 2000) {
                      setErrors(prev => ({
                        ...prev,
                        description: ''
                      }));
                    }
                  } else if (plainText.length > 2000 && plainText.length === currentPlainText.length) {
                    // Allow formatting changes even if at/over limit (tags change but text doesn't)
                    setFormData(prev => ({
                      ...prev,
                      description: value
                    }));
                  }
                }}
                onBlur={() => {
                  // Trigger validation on blur - validate against plain text length
                  const rules = jobValidationRules.description;
                  const plainText = formData.description.replace(/<[^>]*>/g, '').trim();
                  const fieldErrors = validateField('description', plainText, rules);
                  setErrors(prev => ({
                    ...prev,
                    description: fieldErrors.length > 0 ? fieldErrors[0] : ''
                  }));
                }}
                placeholder="Describe the job responsibilities, requirements, and what makes this role exciting..."
                height="300px"
              />
            </div>
            <div className="mt-2">
              <ErrorMessage error={errors.description} />
            </div>
            <div className="mt-2 flex justify-between items-center">
              <p className="text-sm text-gray-500">
                Use the toolbar above to format your job description with headings, lists, links, and more.
              </p>
              <div className="text-right">
                <p className={`text-xs ${formData.description.replace(/<[^>]*>/g, '').length > 2000 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {formData.description.replace(/<[^>]*>/g, '').length}/2000 characters
                </p>
              </div>
            </div>
          </div>

          {/* Requirements Section */}
          <div className="space-y-6 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Requirements ({formData.requirements.length}/30)
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newRequirement}
                    onChange={(e) => {
                      // Allow alphanumeric, scientific, and technical symbols
                      const filteredValue = e.target.value.replace(/[^a-zA-Z0-9\s+/.#,()&_\-:;'"@!*?%[\]<>=\^~|\\]/g, '');
                      setNewRequirement(filteredValue);
                      if (errors.newRequirement) setErrors(prev => ({ ...prev, newRequirement: null }));
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                    maxLength={200}
                    className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.newRequirement ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter a requirement and press Enter or click Add"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addRequirement}
                    disabled={!newRequirement.trim() || formData.requirements.length >= 30}
                  >
                    Add
                  </Button>
                </div>
                {errors.newRequirement && (
                  <p className="mt-1 text-xs text-red-500 font-medium">{errors.newRequirement}</p>
                )}
                <div className="flex justify-end mt-1">
                  <p className="text-xs text-gray-500">
                    {newRequirement.length}/200 characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Common Requirements
                  {loadingRequirements && <span className="ml-2 text-gray-400 font-normal text-xs">Loading...</span>}
                </label>
                <div className="flex flex-wrap gap-2">
                  {commonRequirements.map((req) => (
                    <button
                      key={req}
                      type="button"
                      onClick={() => {
                        const isDuplicate = formData.requirements.some(
                          (r) => r.toLowerCase() === req.toLowerCase()
                        );
                        if (!isDuplicate && formData.requirements.length < 30) {
                          setFormData(prev => ({
                            ...prev,
                            requirements: [...prev.requirements, req]
                          }));
                          if (errors.newRequirement) setErrors(prev => ({ ...prev, newRequirement: null }));
                        } else if (isDuplicate) {
                          setErrors(prev => ({ ...prev, newRequirement: 'This requirement already exists.' }));
                        }
                      }}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 capitalize"
                    >
                      {req}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                {formData.requirements.length > 0 && (
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selected Requirements
                  </label>
                )}
                <div className="flex flex-wrap gap-2">
                  {formData.requirements.map((req, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      <span className="break-words">{req}</span>
                      <button
                        type="button"
                        onClick={() => removeRequirement(index)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <XCircle className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

          {/* Skills Section Styled like Add Candidate */}
          <div className="space-y-6 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Skills ({formData.skills.length}/30)
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => {
                      const filteredValue = e.target.value.replace(/[^a-zA-Z0-9\s+/.#,()&_\-:;'"@!*?%[\]<>=\^~|\\]/g, '');
                      setNewSkill(filteredValue);
                      if (errors.newSkill) setErrors(prev => ({ ...prev, newSkill: null }));
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill(newSkill))}
                    maxLength={200}
                    className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.newSkill ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter skill and press Enter"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => addSkill(newSkill)}
                    disabled={!newSkill.trim() || formData.skills.length >= 30}
                  >
                    Add
                  </Button>
                </div>
                {errors.newSkill && (
                  <p className="mt-1 text-xs text-red-500 font-medium">{errors.newSkill}</p>
                )}
                <div className="flex justify-end mt-1">
                  <p className="text-xs text-gray-500">
                    {newSkill.length}/200 characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Common Skills
                  {loadingSkills && <span className="ml-2 text-gray-400 font-normal text-xs">Loading...</span>}
                </label>
                <div className="flex flex-wrap gap-2">
                  {commonSkills.map((skill) => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => addSkill(skill)}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 capitalize"
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                {formData.skills.length > 0 && (
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selected Skills
                  </label>
                )}
                <div className="flex flex-wrap gap-2">
                  {formData.skills.map((skill) => (
                    <span
                      key={skill}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm capitalize"
                    >
                      {skill}
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            skills: prev.skills.filter((s) => s !== skill),
                          }))
                        }
                        className="ml-2 text-blue-600 hover:text-blue-800 capitalize"
                      >
                        <XCircle className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              disabled={isSaving}
            >
              {isSaving ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Job Post' : 'Create Job Post')}
            </Button>
          </div>
        </form>
      </div>

    </div>
  );
};

export default CreateJob;
